/**
 * Pin geometry and clustering (Issue #13578) — PURE, no DOM, no React.
 *
 * Positioning a pin needs a live measurement; deciding which measured pins
 * collapse into one marker does not. Keeping the second half here means the
 * clustering rule is unit-testable without a layout engine, and means
 * `pin-overlay.tsx` contains only the parts that genuinely need an effect.
 */

import {
  PIN_CLUSTER_RADIUS_PX,
  type FeedbackPinAnchor,
  type PinRect,
} from '@/lib/feedback/pin-anchor'

/** One anchor, measured against the current layout. */
export interface PositionedPin {
  anchor: FeedbackPinAnchor
  /** Viewport-space x of the marker's anchor point. */
  x: number
  /** Viewport-space y of the marker's anchor point. */
  y: number
  /**
   * `false` when `selectorPath` no longer resolved and the position came from
   * the scaled `pinRect` fallback. Drives the "moved" badge.
   */
  resolved: boolean
  /**
   * `false` when the anchor element exists but is not laid out — a collapsed
   * section, `display: none`, a closed tab panel. Such a pin is not rendered
   * (it would land at 0,0) but is still counted: see {@link filterHiddenPins}.
   */
  visible: boolean
}

/** A group of pins rendered as one marker. */
export interface PinCluster {
  /**
   * React key. The FIRST pin's `feedbackId` rather than an index, so a
   * re-layout that produces the same positions produces the same keys and an
   * open popover is not unmounted out from under the user.
   */
  id: string
  x: number
  y: number
  pins: PositionedPin[]
}

/**
 * The marker's DRAWN size, in CSS px — the unconditional `min-h-11 min-w-11`
 * on the button in `feedback-pin.tsx`.
 *
 * The touch target, not the 28px teardrop inside it: the 44x44 box is what
 * leaves the viewport, and shrinking it to make the clamp cheaper would fail
 * the project's touch-target rule for every touch user on every pin.
 */
export const PIN_MARKER_SIZE_PX = 44

/**
 * The viewport a marker has to stay inside. Optional argument to
 * {@link pinAnchorPoint}; when omitted, no clamping happens at all.
 *
 * Coordinates here are VIEWPORT-space, because the overlay layer is
 * `position: fixed`. A caller holding a document-space rect (the stored
 * `PinRect` fallback) must convert BEFORE asking for a clamp — clamping a
 * document coordinate against the viewport would move a pin by the scroll
 * offset.
 */
export interface PinViewportBounds {
  /** `window.innerWidth`. */
  width: number
  /** `window.innerHeight`. */
  height: number
  /**
   * Pixels at the top of the viewport covered by the fixed app header stack
   * (header plus any banner above it). Markers are pushed below it: the
   * overlay is `z-40` and the header `z-50`, so a marker drawn in that band
   * is not merely ugly, it is behind the header and unclickable.
   *
   * Defaults to 0, which still keeps the marker's body on-screen.
   */
  topInset?: number
}

/**
 * Where a marker sits relative to its element: the top-right corner, bounded
 * by `bounds` when one is given.
 *
 * Top-right rather than centre because a centred marker covers the content
 * the feedback is about, and because the top-right of a card is the one
 * corner that is reliably not a control.
 *
 * ## Why the clamp lives here and not in the renderer (Issue #13652)
 *
 * `clusterPins` groups by proximity, so a clamp applied when DRAWING would
 * let two markers that land on the same clamped pixel stay two clusters —
 * the grouping would be computed in coordinates nothing is rendered at.
 * Clamping at the point the coordinate is produced keeps one coordinate
 * space for measuring, clustering and drawing.
 *
 * ## The geometry being bounded
 *
 * The button is drawn `-translate-x-1/2 -translate-y-full` from this point,
 * so it occupies `[x - 22, x + 22] x [y - 44, y]`. Bounding the ANCHOR POINT
 * to `[22, width - 22]` and `[topInset + 44, height]` is therefore exactly
 * "the whole 44x44 box is inside the viewport and below the header".
 *
 * A viewport narrower or shorter than the marker cannot satisfy both edges;
 * the low bound wins, so the marker stays anchored to the top-left of the
 * area rather than disappearing past the far edge.
 *
 * ## The clamp is gated on overlap, per axis
 *
 * Bounding EVERY rect would change what a marker means. An element 4000px
 * down a long page was previously drawn at `y = 4000` — off-screen and
 * harmless; an unconditional clamp parks it on the bottom edge instead,
 * where it has `pointer-events-auto`, covers real content, and points at
 * nothing. Every below-fold pin would pile up there, and every scrolled-past
 * pin under the header.
 *
 * So each axis is clamped only when the rect actually overlaps the viewport
 * on that axis. An anchor that is on screen gets bounded; one that is not
 * keeps its off-screen coordinate and stays out of the way. The axes are
 * independent because a full-bleed element scrolled past the fold overlaps
 * horizontally and not vertically, and its `x` still needs bounding for the
 * frame in which it scrolls back in.
 */
export function pinAnchorPoint(
  rect: PinRect,
  bounds?: PinViewportBounds,
): { x: number; y: number } {
  const x = rect.left + rect.width
  const y = rect.top
  if (!bounds) return { x, y }

  const half = PIN_MARKER_SIZE_PX / 2
  const minX = half
  const maxX = Math.max(minX, bounds.width - half)
  const minY = (bounds.topInset ?? 0) + PIN_MARKER_SIZE_PX
  const maxY = Math.max(minY, bounds.height)

  const overlapsX = rect.left < bounds.width && rect.left + rect.width > 0
  const overlapsY = rect.top < bounds.height && rect.top + rect.height > 0

  return {
    x: overlapsX ? Math.min(Math.max(x, minX), maxX) : x,
    y: overlapsY ? Math.min(Math.max(y, minY), maxY) : y,
  }
}

/**
 * Group pins that sit within `radius` of each other.
 *
 * A single greedy pass: each pin joins the first existing cluster whose
 * ORIGIN it is within, otherwise it starts one. Not k-means and not
 * transitive-closure — both are overkill for a handful of markers, and both
 * move a cluster's position when an unrelated pin joins it, which is what
 * would re-key a marker mid-interaction.
 *
 * Distance is Euclidean. A per-axis test would merge two pins 20px right AND
 * 20px down (28.3px apart), which visibly do not overlap.
 */
export function clusterPins(
  pins: readonly PositionedPin[],
  radius: number = PIN_CLUSTER_RADIUS_PX,
): PinCluster[] {
  const clusters: PinCluster[] = []

  for (const p of pins) {
    if (!p.visible) continue
    const existing = clusters.find(c => {
      const dx = c.x - p.x
      const dy = c.y - p.y
      return Math.sqrt(dx * dx + dy * dy) <= radius
    })
    if (existing) {
      existing.pins.push(p)
    } else {
      clusters.push({ id: p.anchor.feedbackId, x: p.x, y: p.y, pins: [p] })
    }
  }

  return clusters
}

/**
 * The pins that exist on this route but cannot be rendered.
 *
 * The feedback-mode banner badges and lists these (edge case 1, #13653): a
 * thread inside a collapsed section is still a thread, and silently dropping
 * it would make the pin count disagree with the feedback list.
 */
export function filterHiddenPins(
  pins: readonly PositionedPin[],
): PositionedPin[] {
  return pins.filter(p => !p.visible)
}
